import axios, { AxiosInstance, AxiosResponse } from 'axios';
import {
  Device,
  Telemetry,
  User,
  LoginCredentials,
  AuthResponse,
  SpeedAnalytics,
  RealTimeStatus,
  DashboardStats,
  WorkOrder,
  MaintenanceRecord,
  VehicleInspection,
  DriverSafetyScore,
  FuelRecord,
  Geofence,
  GeofenceEvent,
  CostOfOwnership,
  FleetAnalytics,
  Company,
  CompanyRegistration,
  UserInvitation,
  Vehicle,
  FleetVehicle,
  VehicleRequest,
  Alert,
  Driver,
  DriverFormData,
  DriverAnalytics
} from '../types';

const api: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost/api',
  timeout: 30000, // Make sure this is 30000, not 10000
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // MULTI-TENANT HEADER (supports scetru.com + *.localhost dev subdomains)
    const host = window.location.hostname.toLowerCase(); // admin.localhost, t2.scetru.com, localhost

    let tenant = 'app';

    if (host === 'localhost' || host === '127.0.0.1' || host === '::1') {
      tenant = 'app';
    } else if (host.endsWith('.localhost')) {
      // ✅ admin.localhost -> admin
      tenant = host.split('.')[0];
    } else {
      // ✅ t2.scetru.com -> t2
      const parts = host.split('.');
      tenant = parts.length >= 3 ? parts[0] : 'app';
    }

    config.headers['x-platform'] = tenant;
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('company');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Authentication API
export const authAPI = {
  login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
    const response = await api.post('/auth/login', credentials);
    return response.data;
  },

  register: async (userData: Partial<User> & { password: string }): Promise<AuthResponse> => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },

  logout: async (): Promise<void> => {
    await api.post('/auth/logout');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('company');
  },

  registerCompany: async (data: CompanyRegistration): Promise<AuthResponse> => {
    const response = await api.post('/auth/register-company', data);
    return response.data;
  },

  getCurrentUserWithCompany: async (): Promise<{ user: User; company: Company }> => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  inviteUser: async (invitation: UserInvitation): Promise<{ message: string, inviteToken?: string }> => {
    const response = await api.post('/auth/invite-user', invitation);
    return response.data;
  },

  acceptInvitation: async (token: string, password: string): Promise<AuthResponse> => {
    const response = await api.post('/auth/accept-invitation', { token, password });
    return response.data;
  },

  getCompanyUsers: async (): Promise<User[]> => {
    const response = await api.get('/auth/company-users');
    return response.data.data;
  },

  updateUserRole: async (userId: string, role: string): Promise<User> => {
    const response = await api.put(`/auth/users/${userId}/role`, { role });
    return response.data.data;
  },

  deactivateUser: async (userId: string): Promise<void> => {
    await api.put(`/auth/users/${userId}/deactivate`);
  },
};

// Device API
export const deviceAPI = {
  getAllDevices: async (
    params?: { search?: string; status?: string; deviceType?: string; page?: number; limit?: number }
  ): Promise<Device[]> => {
    try {
      const response = await api.get('/devices', { params });
      return response.data.data;
    } catch (error) {
      console.error('Error fetching devices:', error);
      throw error;
    }
  },

  getDeviceById: async (deviceId: string): Promise<Device> => {
    try {
      const response = await api.get(`/devices/${deviceId}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching devices:', error);
      throw error;
    }
  },

  getDeviceByImei: async (imei: string): Promise<Device> => {
    try {
      const response = await api.get(`/devices/imei/${imei}`);
      return response.data.data;
    } catch (error) {
      console.error('Error getDeviceByImei devices:', error);
      throw error;
    }
  },

  fetchVINFromIMEI: async (imei: string): Promise<{
    vin: string | null;
    found: boolean;
    source?: string;
    lastSeen?: string;
  }> => {
    const response = await api.get(`/devices/fetch-vin/${imei}`);
    return response.data;
  },



  quickAddFromDevice: async (data: {
    deviceId: string;
    vin: string;
    plateNumber: string;
    category: 'executive' | 'staff_bus' | 'technical' | 'general';
    make: string;
    model: string;
    year: number;
    color?: string;
    fuelType: 'petrol' | 'diesel' | 'electric' | 'hybrid';
  }): Promise<Vehicle> => {
    const response = await api.post('/vehicles/quick-add-from-device', data);
    return response.data.data;
  },

  checkImeiAvailability: async (
    imei: string
  ): Promise<{ exists: boolean; available: boolean; message: string }> => {
    try {
      const response = await api.get(`/devices/check-imei/${imei}`);
      return response.data.data;
    } catch (error) {
      console.error('Error checkingImeuAvailability device:', error);
      throw error;
    }
  },

  createDevice: async (deviceData: Partial<Device>): Promise<Device> => {
    try {
      const response = await api.post('/devices', deviceData);
      return response.data.data;
    } catch (error) {
      console.error('Error creating device:', error);
      throw error;
    }
  },

  updateDevice: async (deviceId: string, deviceData: Partial<Device>): Promise<Device> => {
    try {
      const response = await api.put(`/devices/${deviceId}`, deviceData);
      return response.data.data;
    } catch (error) {
      console.error('Error updating device:', error);
      throw error;
    }
  },

  deleteDevice: async (deviceId: string): Promise<void> => {
    try {
      const response = await api.delete(`/devices/${deviceId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting device:', error);
      throw error;
    }
  },

  getActiveDevices: async (): Promise<Device[]> => {
    const response = await api.get('/devices/active');
    return response.data.data;
  },
  bindToVehicle: async (deviceId: string, bindingData: {
    vehicleId: string;
    reason: string;
  }): Promise<Device> => {
    const response = await api.post(`/devices/${deviceId}/bind-vehicle`, bindingData);
    return response.data.data;
  },

  getBindingHistory: async (deviceId: string): Promise<Array<{
    vehicleId: string;
    vin: string;
    boundAt: Date;
    unboundAt?: Date;
    boundBy: {
      firstName: string;
      lastName: string;
    };
    reason: string;
  }>> => {
    const response = await api.get(`/devices/${deviceId}/binding-history`);
    return response.data.data;
  },
};

// Telemetry API
export const telemetryAPI = {
  getAllTelemetry: async (): Promise<Telemetry[]> => {
    const response = await api.get('/telemetry/all');
    return response.data.data;
  },

  getLatestTelemetry: async (): Promise<Telemetry[]> => {
    const response = await api.get('/telemetry/latest');
    return response.data.data;
  },

  getUserTelemetry: async (params?: { imei?: string; startDate?: string; endDate?: string }): Promise<Telemetry[]> => {
    const response = await api.get('/telemetry/all', { params });
    return response.data.data;
  },

  getSpeedInfo: async (): Promise<Telemetry[]> => {
    const response = await api.get('/telemetry/speed-info');
    return response.data.data;
  },

  getBatteryInfo: async (): Promise<Telemetry[]> => {
    const response = await api.get('/telemetry/battery-voltage');
    return response.data.data;
  },

  getFuelInfo: async (): Promise<Telemetry[]> => {
    const response = await api.get('/telemetry/fuel');
    return response.data.data;
  },

  getEngineInfo: async (): Promise<Telemetry[]> => {
    const response = await api.get('/telemetry/engine-rpm');
    return response.data.data;
  },

  getTirePressure: async (): Promise<Telemetry[]> => {
    const response = await api.get('/telemetry/tire-pressure');
    return response.data.data;
  },

  getAllDevices: async (
    params?: { search?: string; status?: string; deviceType?: string; page?: number; limit?: number }
  ): Promise<Device[]> => {
    const response = await api.get('/devices', { params });
    return response.data.data;
  },

  getDeviceById: async (deviceId: string): Promise<Device> => {
    const response = await api.get(`/devices/${deviceId}`);
    return response.data.data;
  },

  createDevice: async (deviceData: Partial<Device>): Promise<Device> => {
    const response = await api.post('/devices', deviceData);
    return response.data.data;
  },

  updateDevice: async (deviceId: string, deviceData: Partial<Device>): Promise<Device> => {
    const response = await api.put(`/devices/${deviceId}`, deviceData);
    return response.data.data;
  },

  deleteDevice: async (deviceId: string): Promise<void> => {
    await api.delete(`/devices/${deviceId}`);
  },
  getPlaybackData: async (imei: string, startTime: string, endTime: string) => {
    const response = await api.get(`/telemetry/playback/${imei}`, {
      params: { startTime, endTime }
    });
    return response.data.data;
  },

  // ===== PHASE 2: DEVICE HEALTH MONITORING =====
  getHealthOverview: async (): Promise<{
    overview: {
      healthy: number;
      disconnected: number;
      poor_connection: number;
      critical: number;
      blocked: number;
      no_signal: number;
    };
    unhealthyDevices: Device[];
    totalDevices: number;
  }> => {
    const response = await api.get('/devices/health/overview');
    return response.data.data;
  },

  getDeviceHealth: async (deviceId: string): Promise<{
    deviceId: string;
    imei: string;
    vehicle: any;
    health: {
      lastPing: Date;
      connectionQuality: string;
      signalStrength: number;
      firmwareVersion: string;
      batteryVoltage: number;
      internalTemperature: number;
      diagnosticErrors: Array<{
        code: string;
        message: string;
        severity: string;
        timestamp: Date;
        resolved: boolean;
      }>;
    };
    security: {
      isBlocked: boolean;
      blockReason?: string;
      unauthorizedSwapAlerts: any[];
    };
    healthStatus: string;
    isOnline: boolean;
    bindingStatus: {
      isBound: boolean;
      vehicleId?: string;
      bindingHistory: any[];
    };
  }> => {
    const response = await api.get(`/devices/${deviceId}/health`);
    return response.data.data;
  },

  updateDeviceHealth: async (deviceId: string, healthData: {
    signalStrength?: number;
    batteryVoltage?: number;
    internalTemperature?: number;
    gpsAccuracy?: number;
  }): Promise<void> => {
    await api.post(`/devices/${deviceId}/health`, healthData);
  },

  addDiagnosticError: async (deviceId: string, error: {
    code: string;
    message: string;
    severity: 'low' | 'medium' | 'high' | 'critical';
  }): Promise<void> => {
    await api.post(`/devices/${deviceId}/diagnostic-error`, error);
  },

  resolveDiagnosticError: async (deviceId: string, errorCode: string): Promise<void> => {
    await api.put(`/devices/${deviceId}/resolve-error/${errorCode}`);
  },

  // ===== PHASE 2: VIN-IMEI BINDING SYSTEM =====
  bindDeviceToVehicle: async (deviceId: string, vehicleId: string, reason?: string): Promise<{
    deviceId: string;
    vehicleId: string;
    vin: string;
    imei: string;
  }> => {
    const response = await api.post(`/devices/${deviceId}/bind-vehicle`, {
      vehicleId,
      reason: reason || 'manual_binding'
    });
    return response.data.data;
  },

  unbindDeviceFromVehicle: async (deviceId: string, reason?: string): Promise<void> => {
    await api.post(`/devices/${deviceId}/unbind-vehicle`, {
      reason: reason || 'manual_unbind'
    });
  },

  getUnboundDevices: async (): Promise<Device[]> => {
    const response = await api.get('/devices/binding/unbound');
    return response.data.data;
  },

  getBindingHistory: async (deviceId: string): Promise<Array<{
    vehicleId: string;
    vin: string;
    boundAt: Date;
    unboundAt?: Date;
    boundBy: {
      firstName: string;
      lastName: string;
    };
    reason: string;
  }>> => {
    const response = await api.get(`/devices/binding/history/${deviceId}`);
    return response.data.data;
  },

  // ===== PHASE 2: SECURITY & UNAUTHORIZED ACCESS =====
  getSecurityAlerts: async (): Promise<Array<{
    deviceId: string;
    imei: string;
    plateNumber: string;
    vehicle: any;
    alert: {
      detectedAt: Date;
      expectedVehicleId: string;
      actualLocation: {
        coordinates: [number, number];
      };
      alertSent: boolean;
      resolved: boolean;
    };
  }>> => {
    const response = await api.get('/devices/security/alerts');
    return response.data.data;
  },

  blockDevice: async (deviceId: string, reason: 'unauthorized_swap' | 'theft_suspected' | 'manual_block' | 'maintenance', notes?: string): Promise<void> => {
    await api.post(`/devices/${deviceId}/block`, { reason, notes });
  },

  unblockDevice: async (deviceId: string): Promise<void> => {
    await api.post(`/devices/${deviceId}/unblock`);
  },

  getBlockedDevices: async (): Promise<Device[]> => {
    const response = await api.get('/devices/blocked');
    return response.data.data;
  },

  resolveSecurityAlert: async (deviceId: string, alertId: string, notes?: string): Promise<void> => {
    await api.put(`/devices/${deviceId}/security/resolve-alert/${alertId}`, { notes });
  },

  // ===== PHASE 2: DEVICE PERFORMANCE ANALYTICS =====
  getDevicePerformanceMetrics: async (deviceId: string, period: '24h' | '7d' | '30d' | '90d' = '7d'): Promise<{
    uptime: number;
    avgSignalStrength: number;
    connectionDrops: number;
    dataTransmissionRate: number;
    batteryTrend: Array<{ timestamp: Date; voltage: number }>;
    temperatureTrend: Array<{ timestamp: Date; temperature: number }>;
  }> => {
    const response = await api.get(`/devices/${deviceId}/performance`, {
      params: { period }
    });
    return response.data.data;
  },

  getCompanyDeviceStats: async (): Promise<{
    totalDevices: number;
    activeDevices: number;
    onlineDevices: number;
    boundDevices: number;
    blockedDevices: number;
    maintenanceDue: number;
    healthDistribution: {
      healthy: number;
      warning: number;
      critical: number;
    };
    bindingRate: number;
    avgUptimePercent: number;
  }> => {
    const response = await api.get('/devices/stats');
    return response.data.data;
  },
};

// ===== PHASE 2: VEHICLE MANAGEMENT API =====
// src/services/api.ts

export const vehicleAPI = {
  // Get all vehicles (with optional filters & pagination)
  getAllVehicles: async (params?: {
    category?: 'executive' | 'staff_bus' | 'technical' | 'general';
    status?: 'available' | 'in_use' | 'maintenance' | 'repair' | 'decommissioned';
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<FleetVehicle[]> => {
    try {
      const response = await api.get('/vehicles', { params });

      // ✅ Map _id to id for alvehicleAPIl vehicles
      const vehicles = response.data.data.map((v: any) => ({
        ...v,
        id: v._id,  // ✅ Add id field from _id
        vin: v.vin || null,  // ✅ Handle missing VIN
      }));

      console.log('vehicleAPI.getAllVehicles - mapped vehicles:', vehicles.slice(0, 2));
      return vehicles;
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      throw error;
    }
  },

  quickAddFromDevice: async (data: {
    deviceId: string;
    vin: string;
    plateNumber: string;
    category: 'executive' | 'staff_bus' | 'technical' | 'general';
    make: string;
    model: string;
    year: number;
    color?: string;
    fuelType: 'petrol' | 'diesel' | 'electric' | 'hybrid';
  }): Promise<Vehicle> => {
    const response = await api.post('/vehicles/quick-add-from-device', data);
    return response.data.data;
  },

  // Get vehicle by ID
  getVehicleById: async (vehicleId: string): Promise<Vehicle> => {
    const response = await api.get(`/vehicles/${vehicleId}`);
    return response.data.data;
  },

  // Get vehicle by VIN
  getVehicleByVin: async (vin: string): Promise<Vehicle> => {
    const response = await api.get(`/vehicles/vin/${vin}`);
    return response.data.data;
  },

  // Create a new vehicle
  createVehicle: async (vehicleData: {
    vin: string;
    plateNumber: string;
    make: string;
    model: string;
    year: number;
    category: 'executive' | 'staff_bus' | 'technical' | 'general';
    fuelType: 'petrol' | 'diesel' | 'electric' | 'hybrid';
    color?: string;
    seatingCapacity?: number;
    fuelCapacity?: number;
    assignedDepartment?: string;
    assignedRegion?: string;
    assignedDriver?: string;
    isActive?: boolean;
  }): Promise<Vehicle> => {
    const response = await api.post('/vehicles', vehicleData);
    return response.data.data;
  },

  // Update an existing vehicle
  updateVehicle: async (vehicleId: string, vehicleData: Partial<Vehicle>): Promise<Vehicle> => {
    const response = await api.put(`/vehicles/${vehicleId}`, vehicleData);
    return response.data.data;
  },

  // Delete a vehicle
  deleteVehicle: async (vehicleId: string): Promise<void> => {
    await api.delete(`/vehicles/${vehicleId}`);
  },

  // Vehicle categorization
  getVehiclesByCategory: async (category: 'executive' | 'staff_bus' | 'technical' | 'general'): Promise<Vehicle[]> => {
    const response = await api.get(`/vehicles/category/${category}`);
    return response.data.data;
  },

  // Available vehicles (optionally by date range)
  getAvailableVehicles: async (startDate?: string, endDate?: string): Promise<Vehicle[]> => {
    const response = await api.get('/vehicles/available', {
      params: { startDate, endDate }
    });
    return response.data.data;
  },

  // Vehicle-Device binding
  getUnboundVehicles: async (): Promise<Vehicle[]> => {
    const response = await api.get('/vehicles/binding/unbound');
    return response.data.data;
  },

  getVehicleBindingHistory: async (vehicleId: string): Promise<Array<{
    deviceId: string;
    imei: string;
    boundAt: Date;
    unboundAt?: Date;
    boundBy: {
      firstName: string;
      lastName: string;
    };
    unbindReason?: string;
  }>> => {
    const response = await api.get(`/vehicles/${vehicleId}/binding-history`);
    return response.data.data;
  },

  // Maintenance tracking
  getMaintenanceDue: async (daysAhead: number = 30): Promise<Vehicle[]> => {
    const response = await api.get('/vehicles/maintenance/due', {
      params: { daysAhead }
    });
    return response.data.data;
  },

  scheduleVehicleMaintenance: async (vehicleId: string, maintenanceData: {
    serviceDate: string;
    description: string;
    type: 'service_due' | 'inspection_due' | 'insurance_expiry' | 'registration_expiry';
  }): Promise<void> => {
    await api.post(`/vehicles/${vehicleId}/maintenance/schedule`, maintenanceData);
  },

  completeVehicleMaintenance: async (vehicleId: string, completionData: {
    completedDate: string;
    nextServiceDate?: string;
    mileage?: number;
    notes?: string;
  }): Promise<void> => {
    await api.post(`/vehicles/${vehicleId}/maintenance/complete`, completionData);
  },

  // Vehicle performance and analytics
  getVehiclePerformance: async (vehicleId: string, period: '30d' | '90d' | '6m' | '1y' = '30d'): Promise<{
    utilizationRate: number;
    averageFuelConsumption: number;
    totalDistanceTraveled: number;
    reliabilityScore: number;
    maintenanceCost: number;
    fuelCost: number;
  }> => {
    const response = await api.get(`/vehicles/${vehicleId}/performance`, {
      params: { period }
    });
    return response.data.data;
  },

  getCompanyVehicleStats: async (): Promise<{
    totalVehicles: number;
    activeVehicles: number;
    availableVehicles: number;
    boundVehicles: number;
    maintenanceVehicles: number;
    executiveVehicles: number;
    staffBuses: number;
    technicalVehicles: number;
    generalVehicles: number;
    avgUtilizationRate: number;
    maintenanceDueCount: number;
  }> => {
    const response = await api.get('/vehicles/stats');
    return response.data.data;
  },
};


// ===== PHASE 3: ENHANCED VEHICLE REQUEST API =====
// src/services/api.ts - FIXED VEHICLE REQUEST ENDPOINTS

// Replace your existing vehicleRequestAPI with this corrected version:
export const vehicleRequestAPI = {
  // FIXED: Changed from '/vehicle/requests' to '/vehicles/requests' to match your backend
  getAllRequests: async (params?: {
    status?: 'pending' | 'supervisor_approved' | 'fleet_approved' | 'rejected' | 'completed' | 'cancelled' | 'in_progress';
    requestedBy?: string;
    vehicleCategory?: 'executive' | 'staff_bus' | 'technical' | 'general' | 'any';
    priority?: 'low' | 'medium' | 'high' | 'urgent';
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
  }): Promise<{
    data: VehicleRequest[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> => {
    const response = await api.get('/vehicles/requests', { params });
    return response.data;
  },

  // FIXED: Changed endpoint to match backend
  createRequest: async (requestData: {
    purpose: string;
    destination: string;
    startDateTime: string;
    endDateTime: string;
    passengerCount: number;
    vehicleCategory?: 'executive' | 'staff_bus' | 'technical' | 'general' | 'any';
    specialRequirements?: string;
    priority?: 'low' | 'medium' | 'high' | 'urgent';
  }): Promise<VehicleRequest> => {
    const response = await api.post('/vehicles/requests', requestData);
    return response.data.data;
  },

  // FIXED: Changed endpoint to match backend
  getMyRequests: async (): Promise<VehicleRequest[]> => {
    const response = await api.get('/vehicles/requests/my-requests');
    return response.data.data;
  },

  // FIXED: Changed endpoint to match backend
  getPendingSupervisorRequests: async (): Promise<VehicleRequest[]> => {
    const response = await api.get('/vehicles/requests/pending-supervisor');
    return response.data.data;
  },

  // FIXED: Changed endpoint to match backend
  getPendingFleetRequests: async (): Promise<VehicleRequest[]> => {
    const response = await api.get('/vehicles/requests/pending-fleet');
    return response.data.data;
  },

  // FIXED: Changed endpoint to match backend
  supervisorApprove: async (requestId: string, comments?: string): Promise<VehicleRequest> => {
    const response = await api.put(`/vehicles/requests/${requestId}/supervisor-approve`, { comments });
    return response.data.data;
  },

  // FIXED: Changed endpoint to match backend
  supervisorReject: async (requestId: string, reason: string): Promise<void> => {
    await api.put(`/vehicles/requests/${requestId}/supervisor-reject`, { reason });
  },

  fleetApprove: async (
    requestId:   string,
    vehicleId:   string,
    driverName?: string,
    driverPhone?: string | null,
    comments?:   string,
  ): Promise<VehicleRequest> => {
    const response = await api.put(`/vehicles/requests/${requestId}/fleet-approve`, {
      vehicleId,
      driverName,
      driverPhone,
      comments,
    });
    return response.data.data;
  },

  // FIXED: Changed endpoint to match backend
  fleetReject: async (requestId: string, reason: string): Promise<void> => {
    await api.put(`/vehicles/requests/${requestId}/fleet-reject`, { reason });
  },

  // FIXED: Changed endpoint to match backend
  startTrip: async (requestId: string, actualPassengerCount?: number, startingMileage?: number): Promise<void> => {
    await api.put(`/vehicles/requests/${requestId}/start-trip`, {
      actualPassengerCount,
      startingMileage
    });
  },

  // FIXED: Changed endpoint to match backend
  completeTrip: async (requestId: string, completionData: {
    actualDestination?: string;
    endingMileage?: number;
    fuelUsed?: number;
    driverNotes?: string;
    costs?: {
      fuelCost?: number;
      driverCost?: number;
      maintenanceCost?: number;
      totalActual?: number;
    };
  }): Promise<void> => {
    await api.put(`/vehicles/requests/${requestId}/complete-trip`, completionData);
  },

  cancelTrip: async (requestId: string, reason?: string): Promise<void> => {
    await api.put(`/vehicles/requests/${requestId}/cancel`, { reason });
  },

  // FIXED: Changed endpoint to match backend
  getAvailableVehicles: async (startDateTime: string, endDateTime: string, category?: string): Promise<Vehicle[]> => {
    const response = await api.get('/vehicles/requests/available-vehicles', {
      params: {
        startDateTime,
        endDateTime,
        category
      }
    });
    return response.data.data;
  },

  // FIXED: Changed endpoint to match backend
  getCalendarView: async (startDate?: string, endDate?: string): Promise<Array<{
    id: string;
    title: string;
    start: string;
    end: string;
    status: string;
    requester: string;
    vehicle: string;
  }>> => {
    const response = await api.get('/vehicles/requests/calendar-view', {
      params: { startDate, endDate }
    });
    return response.data.data;
  },

  // FIXED: Changed endpoint to match backend
  getRequestStats: async (period?: '7d' | '30d' | '90d'): Promise<{
    totalRequests: number;
    pendingRequests: number;
    approvedRequests: number;
    completedRequests: number;
    rejectedRequests: number;
    avgProcessingTime: number;
    avgTripDuration: number;
  }> => {
    const response = await api.get('/vehicles/requests/stats', {
      params: { period }
    });
    return response.data.data;
  },

  // FIXED: Added missing utilization report endpoint
  getVehicleUtilizationReport: async (startDate: string, endDate: string): Promise<{
    reportPeriod: {
      startDate: string;
      endDate: string;
      totalHours: number;
    };
    overallStats: {
      totalVehicles: number;
      totalRequests: number;
      averageUtilization: number;
    };
    vehicleUtilization: Array<{
      vehicle: {
        plateNumber: string;
        make: string;
        model: string;
        category: string;
      };
      totalHours: number;
      requestCount: number;
      utilizationPercentage: number;
    }>;
  }> => {
    const response = await api.get('/vehicles/requests/utilization-report', {
      params: { startDate, endDate }
    });
    return response.data.data;
  }
};

// FIXED: Update calendar API endpoints to match backend
export const calendarAPI = {
  // FIXED: Changed to match backend route structure
  checkConflicts: async (startDateTime: string, endDateTime: string, excludeRequestId?: string): Promise<Array<{
    _id: string;
    startDateTime: string;
    endDateTime: string;
    purpose: string;
    requestedBy: {
      firstName: string;
      lastName: string;
    };
    assignedVehicle?: {
      plateNumber: string;
      make: string;
      model: string;
    };
  }>> => {
    const response = await api.get('/vehicles/requests/calendar-conflicts', {
      params: {
        startDateTime,
        endDateTime,
        excludeRequestId
      }
    });
    return response.data.data;
  },

  // FIXED: Added proper vehicle reservation endpoint
  reserveVehicle: async (vehicleId: string, startDateTime: string, endDateTime: string): Promise<{
    vehicleId: string;
    reservedAt: string;
    expiresAt: string;
    reservedBy: string;
  }> => {
    const response = await api.post('/vehicles/requests/reserve-vehicle', {
      vehicleId,
      startDateTime,
      endDateTime
    });
    return response.data.data;
  },

  // FIXED: Add peak usage analysis - need to add this to backend
  getPeakUsageAnalysis: async (startDate: string, endDate: string): Promise<{
    hourlyDistribution: Array<{
      hour: number;
      timeLabel: string;
      requestCount: number;
    }>;
    dailyDistribution: Array<{
      date: string;
      requestCount: number;
    }>;
    categoryDistribution: Array<{
      category: string;
      requestCount: number;
    }>;
    insights: {
      peakHour: {
        hour: number;
        timeLabel: string;
        requestCount: number;
      };
      peakDay: {
        date: string;
        requestCount: number;
      };
      mostRequestedCategory: string;
    };
  }> => {
    // NOTE: This endpoint needs to be added to your backend
    const response = await api.get('/vehicles/requests/peak-usage', {
      params: { startDate, endDate }
    });
    return response.data.data;
  }
};

// ===== PHASE 3: ENHANCED ALERT API =====
export const enhancedAlertAPI = {
  // Get all alerts with advanced filtering
  getAllAlerts: async (params?: {
    type?: 'device_health' | 'security' | 'maintenance' | 'binding' | 'system' | 'vehicle_request';
    subType?: 'vehicle_request' | 'approval_needed' | 'assignment_needed';
    severity?: 'low' | 'medium' | 'high' | 'critical';
    status?: 'active' | 'acknowledged' | 'resolved';
    targetUserId?: string;
    page?: number;
    limit?: number;
  }): Promise<{
    data: Alert[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> => {
    const response = await api.get('/alerts', { params });
    return response.data;
  },

  // Get unread alerts count
  getUnreadCount: async (): Promise<number> => {
    const response = await api.get('/alerts/unread-count');
    return response.data.data.count;
  },

  // Mark alert as acknowledged
  acknowledgeAlert: async (alertId: string): Promise<void> => {
    await api.put(`/alerts/${alertId}/acknowledge`);
  },

  // Mark alert as resolved
  resolveAlert: async (alertId: string, resolution?: string): Promise<void> => {
    await api.put(`/alerts/${alertId}/resolve`, { resolution });
  },

  // Mark multiple alerts as read
  markMultipleAsRead: async (alertIds: string[]): Promise<void> => {
    await api.put('/alerts/mark-multiple-read', { alertIds });
  },

  // Get alert preferences
  getNotificationPreferences: async (): Promise<{
    email: boolean;
    sms: boolean;
    push: boolean;
    quietHours: {
      enabled: boolean;
      startTime: string;
      endTime: string;
    };
    alertTypes: string[];
  }> => {
    const response = await api.get('/alerts/preferences');
    return response.data.data;
  },

  // Update alert preferences
  updateNotificationPreferences: async (preferences: {
    email?: boolean;
    sms?: boolean;
    push?: boolean;
    quietHours?: {
      enabled: boolean;
      startTime: string;
      endTime: string;
    };
    alertTypes?: string[];
  }): Promise<void> => {
    await api.put('/alerts/preferences', preferences);
  }
};

// ===== PHASE 2: ALERT & NOTIFICATION API =====
export const alertAPI = {
  getAllAlerts: async (params?: {
    type?: 'device_health' | 'security' | 'maintenance' | 'binding' | 'system';
    severity?: 'low' | 'medium' | 'high' | 'critical';
    status?: 'active' | 'acknowledged' | 'resolved';
    page?: number;
    limit?: number;
  }): Promise<Alert[]> => {
    const response = await api.get('/alerts', { params });
    return response.data.data;
  },

  acknowledgeAlert: async (alertId: string): Promise<void> => {
    await api.put(`/alerts/${alertId}/acknowledge`);
  },

  resolveAlert: async (alertId: string, resolution?: string): Promise<void> => {
    await api.put(`/alerts/${alertId}/resolve`, { resolution });
  },

  getUnreadAlerts: async (): Promise<Alert[]> => {
    const response = await api.get('/alerts/unread');
    return response.data.data;
  },

  markAllAsRead: async (): Promise<void> => {
    await api.put('/alerts/mark-all-read');
  },

  // Alert preferences
  getAlertPreferences: async (): Promise<{
    emailAlerts: boolean;
    smsAlerts: boolean;
    pushAlerts: boolean;
    alertTypes: string[];
    quietHours: {
      enabled: boolean;
      startTime: string;
      endTime: string;
    };
  }> => {
    const response = await api.get('/alerts/preferences');
    return response.data.data;
  },

  updateAlertPreferences: async (preferences: any): Promise<void> => {
    await api.put('/alerts/preferences', preferences);
  },
};


// Speed Analytics API
export const speedAnalyticsAPI = {
  getSpeedReport: async (imei: string): Promise<SpeedAnalytics> => {
    const response = await api.get(`/dashboard/speed/${imei}`);
    return response.data.data;
  },

  getCurrentSpeedData: async (imei: string): Promise<SpeedAnalytics> => {
    const response = await api.get(`/dashboard/speed/${imei}/current`);
    return response.data.data;
  },

  getSpeedViolations: async (minSpeed: number = 80): Promise<Telemetry[]> => {
    const response = await api.get('/telemetry/speed-info', {
      params: { minSpeed }
    });
    return response.data.data;
  },
};

// Real-time API
export const realtimeAPI = {
  getStatus: async (): Promise<RealTimeStatus> => {
    const response = await api.get('/dashboard/realtime/status');
    return response.data.data;
  },

  getConnectedDevices: async (): Promise<string[]> => {
    const response = await api.get('/dashboard/realtime/status');
    return response.data.data.connectedDevices;
  },
};

// Dashboard API
export const dashboardAPI = {
  getStats: async (): Promise<DashboardStats> => {
    const response = await api.get('/dashboard/stats');
    return response.data.data;
  },

  getFleetAnalytics: async (): Promise<FleetAnalytics> => {
    const response = await api.get('/dashboard/analytics');
    return response.data.data;
  },

  getCostOfOwnership: async (deviceId?: string): Promise<CostOfOwnership[]> => {
    const response = await api.get('/dashboard/cost-of-ownership', {
      params: { deviceId }
    });
    return response.data.data;
  },
};

// Work Orders API
export const workOrderAPI = {
  getAllWorkOrders: async (params?: {
    status?: string;
    priority?: string;
    deviceId?: string;
    page?: number;
    limit?: number;
  }): Promise<WorkOrder[]> => {
    const response = await api.get('/work-orders', { params });
    return response.data.data;
  },

  getWorkOrderById: async (id: string): Promise<WorkOrder> => {
    const response = await api.get(`/work-orders/${id}`);
    return response.data.data;
  },

  createWorkOrder: async (workOrder: Partial<WorkOrder>): Promise<WorkOrder> => {
    const response = await api.post('/work-orders', workOrder);
    return response.data.data;
  },

  updateWorkOrder: async (id: string, workOrder: Partial<WorkOrder>): Promise<WorkOrder> => {
    const response = await api.put(`/work-orders/${id}`, workOrder);
    return response.data.data;
  },

  deleteWorkOrder: async (id: string): Promise<void> => {
    await api.delete(`/work-orders/${id}`);
  },
};

// Maintenance API
export const maintenanceAPI = {
  getAllMaintenance: async (params?: {
    deviceId?: string;
    type?: string;
    status?: string;
    page?: number;
    limit?: number;
  }): Promise<MaintenanceRecord[]> => {
    const response = await api.get('/maintenance', { params });
    return response.data.data;
  },

  createMaintenance: async (maintenance: Partial<MaintenanceRecord>): Promise<MaintenanceRecord> => {
    const response = await api.post('/maintenance', maintenance);
    return response.data.data;
  },

  updateMaintenance: async (id: string, maintenance: Partial<MaintenanceRecord>): Promise<MaintenanceRecord> => {
    const response = await api.put(`/maintenance/${id}`, maintenance);
    return response.data.data;
  },

  getOverdueMaintenance: async (): Promise<MaintenanceRecord[]> => {
    const response = await api.get('/maintenance/overdue');
    return response.data.data;
  },
};

// Vehicle Inspection API
export const inspectionAPI = {
  getAllInspections: async (params?: {
    deviceId?: string;
    status?: string;
    type?: string;
    page?: number;
    limit?: number;
  }): Promise<VehicleInspection[]> => {
    const response = await api.get('/inspections', { params });
    return response.data.data;
  },

  createInspection: async (inspection: Partial<VehicleInspection>): Promise<VehicleInspection> => {
    const response = await api.post('/inspections', inspection);
    return response.data.data;
  },

  updateInspection: async (id: string, inspection: Partial<VehicleInspection>): Promise<VehicleInspection> => {
    const response = await api.put(`/inspections/${id}`, inspection);
    return response.data.data;
  },
};

// Driver Safety API
export const safetyAPI = {
  getSafetyScores: async (params?: {
    deviceId?: string;
    period?: string;
    page?: number;
    limit?: number;
  }): Promise<DriverSafetyScore[]> => {
    const response = await api.get('/safety/scores', { params });
    return response.data.data;
  },

  getAggressiveDrivers: async (limit: number = 5): Promise<DriverSafetyScore[]> => {
    const response = await api.get('/safety/aggressive-drivers', {
      params: { limit }
    });
    return response.data.data;
  },

  getSeatbeltViolations: async (limit: number = 5): Promise<DriverSafetyScore[]> => {
    const response = await api.get('/safety/seatbelt-violations', {
      params: { limit }
    });
    return response.data.data;
  },
};

// Fuel API
export const fuelAPI = {
  getAllFuelRecords: async (params?: {
    deviceId?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
  }): Promise<FuelRecord[]> => {
    const response = await api.get('/fuel/records', { params });
    return response.data.data;
  },

  getFuelTrends: async (period: string = '3months'): Promise<any[]> => {
    const response = await api.get('/fuel/trends', {
      params: { period }
    });
    return response.data.data;
  },

  getFuelEfficiency: async (deviceId?: string): Promise<number> => {
    const response = await api.get('/fuel/efficiency', {
      params: { deviceId }
    });
    return response.data.data;
  },
};

// Geofence API
export const geofenceAPI = {
  getAllGeofences: async (): Promise<Geofence[]> => {
    const response = await api.get('/geofences');
    return response.data.data;
  },

  createGeofence: async (geofence: Partial<Geofence>): Promise<Geofence> => {
    const response = await api.post('/geofences', geofence);
    return response.data.data;
  },

  updateGeofence: async (id: string, geofence: Partial<Geofence>): Promise<Geofence> => {
    const response = await api.put(`/geofences/${id}`, geofence);
    return response.data.data;
  },

  deleteGeofence: async (id: string): Promise<void> => {
    await api.delete(`/geofences/${id}`);
  },

  getGeofenceEvents: async (params?: {
    geofenceId?: string;
    deviceId?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
  }): Promise<GeofenceEvent[]> => {
    const response = await api.get('/geofences/events', { params });
    return response.data.data;
  },
};


// ===== REAL ANALYTICS API =====
export const analyticsAPI = {

  getDashboardInitialLoad: async (period: string = '30d'): Promise<{
    devices: Device[];
    summary: any;
    mileage: any;
    fuel: any;
    engine: any;
    idle: any;
  }> => {
    const response = await api.get('/analytics/dashboard/initial-load', {
      params: { period }
    });
    return response.data.data;
  },

  // NEW: Single endpoint for analytics page
  getAnalyticsInitialLoad: async (period: string = '7d'): Promise<{

    report: any;
    fuel: any;
    mileage: any;
    engine: any;
    idle: any;
    battery: any;
    trip: any;
    theft: any;
  }> => {
    const response = await api.get('/analytics/analytics/initial-load', {
      params: { period }
    });

    return response.data.data;
  },


  // Dashboard Summary
  getDashboardSummary: async (): Promise<{
    fleet: {
      totalVehicles: number;
      healthyVehicles: number;
      warningVehicles: number;
      criticalVehicles: number;
    };
    fuel: {
      totalConsumed: number;
      avgConsumptionPerDay: number;
      theftAlertsLast24h: number;
    };
    mileage: {
      totalMileage: number;
      avgMileagePerVehicle: number;
    };
    efficiency: {
      totalIdleTime: number;
      totalTrips: number;
    };
  }> => {
    const response = await api.get('/analytics/dashboard-summary');
    return response.data.data;
  },

  // Fuel Analytics
  getFuelConsumptionTrend: async (period: string = '30d'): Promise<Array<{
    deviceId: string;
    date: string;
    avgFuelLevel: number;
    fuelConsumed: number;
    fuelAdded: number;
    minLevel: number;
    maxLevel: number;
    device?: {
      imei: string;
      plateNumber: string;
      make: string;
      model: string;
    };
  }>> => {
    const response = await api.get('/analytics/fuel/consumption-trend', {
      params: { period }
    });
    return response.data.data;
  },

  detectFuelTheft: async (lookbackHours: number = 24): Promise<{
    data: Array<{
      deviceId: string;
      detectedAt: Date;
      fuelBefore: number;
      fuelAfter: number;
      amountLost: number;
      suspicionLevel: 'high' | 'medium';
      device?: any;
    }>;
    summary: {
      totalEvents: number;
      highSuspicion: number;
      totalFuelLoss: number;
    };
  }> => {
    const response = await api.get('/analytics/fuel/theft-detection', {
      params: { lookbackHours }
    });
    return response.data;
  },

  // Engine Health Analytics
  getEngineHealthScores: async (): Promise<{
    data: Array<{
      deviceId: string;
      imei: string;
      healthScore: number;
      status: 'healthy' | 'warning' | 'critical';
      issues: Array<{
        type: string;
        severity: string;
        message: string;
        value: any;
      }>;
      metrics: {
        dtcCount: number;
        coolantTemp: number | null;
        batteryVoltage: string | null;
        engineLoad: number | null;
        engineRPM: number | null;
        satelliteCount: number | null;
      };
      lastUpdate: Date;
      device?: any;
    }>;
    summary: {
      healthy: number;
      warning: number;
      critical: number;
      avgScore: number;
    };
  }> => {
    const response = await api.get('/analytics/engine/health-scores');
    return response.data;
  },

  // Mileage Analytics
  getFleetMileageTrend: async (period: string = '7d'): Promise<{
    byDevice: Array<{
      date: string;
      deviceId: string;
      dailyMileage: number;
      readings: number;
    }>;
    fleetDaily: Array<{
      date: string;
      totalMileage: number;
      vehicleCount: number;
      avgMileagePerVehicle: number;
    }>;
  }> => {
    const response = await api.get('/analytics/mileage/fleet-trend', {
      params: { period }
    });
    return response.data.data;
  },

  // Idle Time Analytics
  getIdleAnalysis: async (period: string = '7d'): Promise<{
    data: Array<{
      deviceId: string;
      idleEvents: number;
      totalIdleMinutes: number;
      avgRPMWhileIdle: number;
      device?: any;
    }>;
    summary: {
      totalIdleMinutes: number;
      totalIdleHours: number;
      avgIdlePerVehicle: number;
    };
  }> => {
    const response = await api.get('/analytics/idle/analysis', {
      params: { period }
    });
    return response.data;
  },

  // Battery Health Analytics
  getBatteryHealthTrends: async (period: string = '7d'): Promise<{
    data: Array<{
      deviceId: string;
      readings: Array<{
        timestamp: Date;
        voltage: number;
        ignition: boolean;
      }>;
      avgVoltage: string;
      minVoltage: number;
      maxVoltage: number;
      healthStatus: 'healthy' | 'low' | 'critical' | 'overcharging';
      device?: any;
    }>;
    summary: {
      healthy: number;
      low: number;
      critical: number;
      overcharging: number;
    };
  }> => {
    const response = await api.get('/analytics/battery/health-trends', {
      params: { period }
    });
    return response.data;
  },

  // Trip Analytics
  getTripAnalysis: async (period: string = '7d'): Promise<{
    data: Array<{
      deviceId: string;
      tripCount: number;
      totalDistance: number;
      totalDurationMinutes: number;
      avgTripDistance: number;
      avgTripDuration: number;
      device?: any;
    }>;
    summary: {
      totalTrips: number;
      totalDistance: number;
      totalDuration: number;
    };
  }> => {
    const response = await api.get('/analytics/trips/analysis', {
      params: { period }
    });
    return response.data;
  },

  // Comprehensive Fleet Report
  getComprehensiveReport: async (period: string = '7d'): Promise<{
    generatedAt: Date;
    period: string;
    fleet: {
      totalVehicles: number;
      activeVehicles: number;
    };
    health: {
      healthy: number;
      warning: number;
      critical: number;
      avgHealthScore: number;
      topIssues: Record<string, number>;
    };
    fuel: {
      totalConsumed: number;
      avgDailyConsumption: number;
      totalRefueled: number;
    };
    mileage: {
      totalMileage: number;
      avgMileagePerVehicle: number;
      dailyTrend: any[];
    };
    efficiency: {
      totalIdleTime: number;
      avgIdlePerVehicle: number;
      totalTrips: number;
      totalTripDistance: number;
      avgTripDistance: number;
    };
    battery: {
      healthy: number;
      needsAttention: number;
      avgVoltage: string;
    };
  }> => {
    const response = await api.get('/analytics/fleet/comprehensive-report', {
      params: { period }
    });
    return response.data.data;
  }
};



// Driver API
export const driverAPI = {
  getAllDrivers: async (params?: { status?: string; search?: string }): Promise<Driver[]> => {
    const response = await api.get('/drivers', { params });
    return response.data.data;
  },

  getDriver: async (id: string): Promise<Driver> => {
    const response = await api.get(`/drivers/${id}`);
    return response.data.data;
  },

  createDriver: async (driverData: DriverFormData): Promise<Driver> => {
    const response = await api.post('/drivers', driverData);
    return response.data.data;
  },

  updateDriver: async (id: string, driverData: Partial<DriverFormData>): Promise<Driver> => {
    const response = await api.put(`/drivers/${id}`, driverData);
    return response.data.data;
  },

  deleteDriver: async (id: string): Promise<void> => {
    await api.delete(`/drivers/${id}`);
  },

  // ✅ UPDATED: Assign vehicle (can assign multiple)
  assignVehicle: async (driverId: string, vehicleId: string, isPrimary: boolean = false): Promise<Driver> => {
    const response = await api.post(`/drivers/${driverId}/assign-vehicle`, { vehicleId, isPrimary });
    return response.data.data;
  },

  // ✅ UPDATED: Unassign specific vehicle
  unassignVehicle: async (driverId: string, vehicleId: string): Promise<Driver> => {
    const response = await api.post(`/drivers/${driverId}/unassign-vehicle`, { vehicleId });
    return response.data.data;
  },

  // ✅ NEW: Set primary vehicle
  setPrimaryVehicle: async (driverId: string, vehicleId: string): Promise<Driver> => {
    const response = await api.post(`/drivers/${driverId}/set-primary-vehicle`, { vehicleId });
    return response.data.data;
  },

  // ✅ NEW: Get driver's vehicles
  getDriverVehicles: async (driverId: string): Promise<{
    driverId: string;
    driverName: string;
    vehicles: Array<{
      vehicleId: Device;
      assignedAt: Date;
      isPrimary: boolean;
    }>;
    vehicleCount: number;
    primaryVehicle: string;
  }> => {
    const response = await api.get(`/drivers/${driverId}/vehicles`);
    return response.data.data;
  },
  calculateStats: async (): Promise<{ [driverId: string]: { trips: number; distance: number } }> => {
    const response = await api.get('/drivers/stats/calculate');
    return response.data.data;
  },

  getDriverAnalytics: async (driverId: string): Promise<DriverAnalytics> => {
    const response = await api.get(`/drivers/${driverId}/analytics`);
    return response.data.data;
  }
};


// ===== GEOFENCING API =====

export const geofencingAPI = {
  // Get all vehicles with geofencing status
  getAllVehicles: () => api.get('/geofencing/vehicles'),

  // Get zones for a specific vehicle
  getVehicleZones: (vehicleId: string) =>
    api.get(`/geofencing/vehicles/${vehicleId}/zones`),

  // Create new geofence zone
  createZone: (vehicleId: string, zoneData: {
    name: string;
    type: 'circular' | 'polygon';
    center?: { type: string; coordinates: [number, number] };
    radius?: number;
    polygon?: { type: string; coordinates: Array<Array<[number, number]>> };
    rules?: {
      allowEntry?: boolean;
      allowExit?: boolean;
      alertOnEntry?: boolean;
      alertOnExit?: boolean;
      alertOnBreach?: boolean;
      alertOnSpeedViolation?: boolean;
      speedLimit?: number;
    };
    activeSchedule?: any;
    color?: string;
    description?: string;
  }) => api.post(`/geofencing/vehicles/${vehicleId}/zones`, zoneData),

  // Update existing zone
  updateZone: (vehicleId: string, zoneId: string, updates: {
    name?: string;
    radius?: number;
    polygon?: { type: string; coordinates: Array<Array<[number, number]>> };
    rules?: any;
    activeSchedule?: any;
    color?: string;
    description?: string;
    isActive?: boolean;
  }) => api.put(`/geofencing/vehicles/${vehicleId}/zones/${zoneId}`, updates),

  // Delete zone
  deleteZone: (vehicleId: string, zoneId: string) =>
    api.delete(`/geofencing/vehicles/${vehicleId}/zones/${zoneId}`),

  // Toggle geofencing for vehicle
  toggleGeofencing: (vehicleId: string, enabled: boolean) =>
    api.put(`/geofencing/vehicles/${vehicleId}/toggle`, { enabled }),

  // Get geofence status for all vehicles
  getStatus: () => api.get('/geofencing/status'),

  // Check vehicle geofence status now
  checkVehicleNow: (vehicleId: string) =>
    api.post(`/geofencing/vehicles/${vehicleId}/check-now`),

  // Get violations (all or filtered)
  getViolations: (params?: {
    vehicleId?: string;
    startDate?: string;
    endDate?: string;
    violationType?: string;
    acknowledged?: string;
    page?: number;
    limit?: number;
  }) => api.get('/geofencing/violations', { params }),

  // Get violations for specific vehicle
  getVehicleViolations: (vehicleId: string) =>
    api.get(`/geofencing/vehicles/${vehicleId}/violations`),

  // Acknowledge violation
  acknowledgeViolation: (vehicleId: string, violationId: string, notes?: string) =>
    api.put(
      `/geofencing/vehicles/${vehicleId}/violations/${violationId}/acknowledge`,
      { notes }
    ),

  // Get statistics
  getStatistics: (params?: { startDate?: string; endDate?: string }) =>
    api.get('/geofencing/statistics', { params }),


  checkOverlap: (vehicleId: string, zoneData: any) =>
    api.post(`/geofencing/vehicles/${vehicleId}/zones/check-overlap`, zoneData),

  // ENHANCEMENT 2: Templates
  getTemplates: () => api.get('/geofencing/templates'),
  createFromTemplate: (vehicleId: string, templateData: any) =>
    api.post(`/geofencing/vehicles/${vehicleId}/zones/from-template`, templateData),

  // ENHANCEMENT 3: Bulk Management
  bulkApply: (vehicleIds: string[], zoneData: any) =>
    api.post('/geofencing/zones/bulk-apply', { vehicleIds, zoneData }),
  bulkDelete: (vehicleIds: string[], zoneName?: string) =>
    api.post('/geofencing/zones/bulk-delete', { vehicleIds, zoneName }),

  // ENHANCEMENT 4: Export
  exportViolations: (format: 'csv' | 'json', params?: any) =>
    api.get('/geofencing/violations/export', { params: { format, ...params } }),

  // ENHANCEMENT 5: Prediction
  predictViolations: (vehicleId: string) =>
    api.get(`/geofencing/vehicles/${vehicleId}/predict`),

  // ENHANCEMENT 6: Analytics
  getAnalytics: (params?: { startDate?: string; endDate?: string }) =>
    api.get('/geofencing/analytics', { params }),

};

// ===== END OF GEOFENCING API =====




export const platformBillingAPI = {
  getOverview: async () => {
    const res = await api.get('/platform-admin/billing/overview');
    return res.data.data;
  },

  getInvoices: async (params?: { status?: string; page?: number; limit?: number; search?: string }) => {
    const res = await api.get('/platform-admin/billing/invoices', { params });
    return res.data; // { success, data, pagination }
  },

  getInvoice: async (invoiceId: string) => {
    const res = await api.get(`/platform-admin/billing/invoices/${invoiceId}`);
    return res.data.data;
  },

  getInvoicePdf: async (invoiceId: string) => {
    const res = await api.get(`/platform-admin/billing/invoices/${invoiceId}/pdf`);
    return res.data.data; // { pdfUrl }
  }
};
export const portalAPI = {
  // Get current portal token + full URL
  getPortalToken: async (): Promise<{
    portalToken: string;
    portalUrl: string;
  }> => {
    const response = await api.get('/companies/portal-token');
    return response.data.data;
  },
 
  // Regenerate token — invalidates current link immediately
  regeneratePortalToken: async (): Promise<{
    portalToken: string;
    portalUrl: string;
  }> => {
    const response = await api.post('/companies/portal-token/regenerate');
    return response.data.data;
  },
};


export default api;